/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
	useState,
	useRef,
	FC,
	ForwardRefExoticComponent,
	ReactChild,
	useEffect,
} from "react";
import { cn } from "../../lib/src/utils/shorthands";
import { Knob, KnobType } from "./docs-types";
import { useStyeguideCtx } from "./docs-layout";
import { ExampleRender } from "./example-render";
import { DocsTable } from "./docs-table";
import * as scss from "./knobs.scss";

type KnobsProps<T extends {}> = {
	component: FC<T> | (ForwardRefExoticComponent<T> & { __knobName?: string });
	initialProps: T | ((acts: InitActions<T>) => T);
	exampleValues?: ExampleValues<T>;
	customBar?: (p: { id: string }) => ReactChild;
};

type ExampleValues<T extends {}> = {
	[K in keyof T]?: Record<string, T[K]>;
};

type InitActions<T extends {}> = {
	getPrevState: () => T | null;
	setState: (state: Partial<T>) => void;
};

let idCounter = 0;

export function Knobs<T extends {}>(props: KnobsProps<T>): JSX.Element | null {
	const { component, initialProps, exampleValues = {} as ExampleValues<T>, customBar } = props;

	const name = ("__knobName" in component && component.__knobName) || component.name;

	let ourSpec = useStyeguideCtx().knobSpecs.find((ks) => ks.componentName === name);

	if (ourSpec === undefined) {
		if (Object.keys(initialProps).length === 0) {
			//There won't be a spec if we don't have any props
			ourSpec = { componentName: name, knobs: [] };
		} else {
			throw new Error(`Could not find Knob spec for component: ${name}`);
		}
	}

	const [componentProps, setComponentProps] = useState<T | null>(null); //null is a placeholder for the first render.
	const [displayProps, setDisplayProps] = useState<T | null>(null);
	const prevState = useRef(componentProps);

	useEffect(() => {
		prevState.current = componentProps;
	}, [componentProps]);

	//Sets boths states and accepts merges input with existing state
	const setProps = (state: Partial<T>) => {
		setComponentProps((s) => ({ ...s, ...(state as any) }));
		setDisplayProps((s) => ({ ...s, ...(state as any) }));
	};

	//This code bootstraps the initial state.
	//We need this bootstrap step because initialProps might depend on the 'setState'
	//So state is initialy null, we get access to the setter, and then we set the actual initial state
	if (componentProps === null || displayProps === null) {
		const initialComponentProps =
			typeof initialProps === "function"
				? (initialProps as (acts: InitActions<T>) => T)({
						getPrevState: () => prevState.current,
						setState: setProps,
				  })
				: initialProps;

		setComponentProps(initialComponentProps);
		setDisplayProps(defaultKnobsState(ourSpec.knobs, initialComponentProps, exampleValues));
		return null;
	}

	//Toggle a single property in componentProps between not being defined and the last value set
	//e.g. we have componentProps: `{foo: 42, bar: "v"}`, calling toggleProp("foo") will change the state to `{bar: "v"}`, calling toggleProp("foo") again will change it back to `{foo: 42, bar: "v"}`
	const toggleProp = (property: keyof T) => {
		const next = { ...componentProps };
		if (next[property] === undefined) {
			next[property] = displayProps[property];
		} else {
			delete next[property];
		}
		setComponentProps(next);
	};

	const id = `knobs-${idCounter++}`;

	const anyKnobs = !!ourSpec.knobs.length;
	return (
		<div id={id} className={scss.knobsContainer}>
			<ExampleRender
				code={knobCode(component.displayName ?? ourSpec.componentName, componentProps)}
				showCodeInitial={!anyKnobs}
				props={componentProps}
				customBar={customBar && customBar({ id })}
			>
				{React.createElement(component, componentProps)}
			</ExampleRender>
			{anyKnobs && (
				<DocsTable border="top" standalone={false}>
					<thead>
						<tr>
							<th>Property</th>
							<th>Description</th>
							<th>Defined</th>
							<th>Value</th>
						</tr>
					</thead>
					<tbody>
						{ourSpec.knobs.map((knob) => {
							const property = knob.property as keyof T;
							const value = componentProps[property];
							const setValue = (val: any) => setProps({ [property]: val } as any);
							const isUnset = knob.isOptional && value === undefined;

							const propertyId = `${id}-${String(property)}`;

							const examples = exampleValues[property];
							const input = examples
								? knobExampleInput(examples, setValue, propertyId, isUnset)
								: knobInput(knob, displayProps[property], setValue, propertyId, isUnset);

							const disableDefineToggle =
								!knob.isOptional /* isRequired */ ||
								(knobDefaultValue(knob) === undefined && !examples);

							return (
								<tr
									key={knob.property}
									className={cn(value !== undefined && DocsTable.highlightTrClass)}
								>
									<td>
										<div id={propertyId} className={scss.bigsmally}>
											<span>{knob.property}</span>
											<span>{knob.isOptional ? "Optional" : "Required"}</span>
										</div>
									</td>
									<td>
										<div className={scss.bigsmally}>
											<code>{knobType(knob)}</code>
											<span>{knob.jsDoc}</span>
										</div>
									</td>
									<td>
										<div>
											<RadioGuy
												value="Yes"
												checked={value !== undefined}
												setValue={() => toggleProp(property)}
												disabled={disableDefineToggle}
											/>
											<RadioGuy
												value="No"
												checked={value === undefined}
												setValue={() => toggleProp(property)}
												disabled={disableDefineToggle}
											/>
										</div>
									</td>
									<td>{input}</td>
								</tr>
							);
						})}
					</tbody>
				</DocsTable>
			)}
		</div>
	);
}

function knobInput(
	knob: Knob,
	value: any,
	setValue: (v: any) => void,
	labelledby: string,
	disabled: boolean
): JSX.Element | null {
	switch (knob.type) {
		case "boolean":
			return (
				<div style={{ display: "flex" }}>
					<RadioGuy value={true} checked={value === true} setValue={setValue} disabled={disabled} />
					<RadioGuy
						value={false}
						checked={value === false}
						setValue={setValue}
						disabled={disabled}
					/>
				</div>
			);
		case "string":
		case "element":
			return (
				<input
					type="text"
					aria-labelledby={labelledby}
					value={value ?? ""}
					onChange={(e) => setValue(e.target.value)}
					disabled={disabled}
				/>
			);
		case "number":
			return (
				<input
					type="number"
					aria-labelledby={labelledby}
					value={value?.toString() ?? ""}
					onChange={(e) => {
						const v = Number(e.target.value);
						if (Number.isFinite(v)) setValue(v);
					}}
					disabled={disabled}
				/>
			);
		case "literal-union":
			return (
				<select
					aria-labelledby={labelledby}
					value={value}
					onChange={(e) => setValue(e.target.value)}
					disabled={disabled}
				>
					{knob.values.map((v) => (
						<option key={v}>{v}</option>
					))}
				</select>
			);
		case "array":
		case "type-union":
		case "ReactNode":
		case "object":
		case "date":
		case "null":
		case "function":
		case "unknown":
			return null;
	}
	exhaustiveMatch(knob);
}

function knobExampleInput<T extends {}>(
	examples: ExampleValues<T>[keyof T],
	setValue: (v: any) => void,
	labelledby: string,
	disabled: boolean
) {
	return (
		<select
			aria-labelledby={labelledby}
			onChange={(e) => setValue(examples![e.target.value])}
			disabled={disabled}
		>
			{Object.keys(examples!).map((e) => (
				<option key={e}>{e}</option>
			))}
		</select>
	);
}

function knobType(knob: KnobType): string {
	switch (knob.type) {
		case "literal-union":
			return knob.values.map((v) => (typeof v === "string" ? `"${v}"` : v)).join(" | ");
		case "type-union":
			return knob.types.map(knobType).join(" | ");
		case "array":
			return `${knob.elementType}[]`;
		default:
			return knob.type;
	}
}

function knobDefaultValue(knob: Knob) {
	switch (knob.type) {
		case "boolean":
			return true;
		case "string":
		case "element":
			return "";
		case "number":
			return 0;
		case "literal-union":
			return knob.values[0];
		case "null":
			return null;
		case "array":
		case "type-union":
		case "ReactNode":
		case "object":
		case "date":
		case "function":
		case "unknown":
			return undefined;
	}
	exhaustiveMatch(knob);
}

function defaultKnobsState<T extends {}>(
	knobs: Knob[],
	initialProps: T,
	exampleValues: ExampleValues<T>
) {
	const es = knobs.map((knob) => {
		const prop = knob.property as keyof T;
		let val: any = initialProps[prop];
		if (val !== undefined) {
			return [prop, val];
		}
		val = knobDefaultValue(knob);
		if (val === undefined) {
			const exampleValue = exampleValues[prop] && Object.values(exampleValues[prop]!)[0];
			val = exampleValue !== undefined ? exampleValue : val;
		}
		return [prop, val];
	});

	return Object.fromEntries(es) as unknown as T;
}

function knobCode(name: string, props: any): string {
	const attrs = Object.entries(props)
		.map(([key, value]) => {
			if (value === undefined || key === "children") {
				return "";
			}
			switch (typeof value) {
				case "string":
					return `${key}="${value}"`;
				case "object":
					return `${key}={${JSON.stringify(value, (_key, value) =>
						typeof value == "object" ? {} : value
					)}}`;
				case "function":
					return `${key}={() => {...}}`;
				default:
					return `${key}={${value}}`;
			}
		})
		.filter(Boolean);
	const oneLiner = (attrs.join(" ") + name).length < 80;
	let r = `<${name}`;
	if (attrs.length) {
		if (oneLiner) {
			r += " " + attrs.join(" ");
		} else {
			r += "\n\t" + attrs.join("\n\t") + "\n";
		}
	}

	if ("children" in props && props.children !== undefined) {
		r += ">";
		const typeofKid = typeof props.children;
		if (typeofKid === "string") {
			r += props.children.includes("\n") ? "{`" + props.children + "`}" : props.children;
		} else if (typeofKid === "object") {
			r += "{/* the magic of kids */}";
		} else {
			r += `{${props.children}}`;
		}
		r += `</${name}>`;
	} else {
		r += oneLiner ? ` />` : `/>`;
	}
	return r;
}

const exhaustiveMatch = (v: never) => {
	throw v;
};

const RadioGuy = ({
	value,
	checked,
	disabled,
	setValue,
}: {
	value: any;
	setValue: (v: any) => void;
	checked: boolean;
	disabled: boolean;
}) => (
	<label
		style={{
			paddingRight: 8,
			display: "inline-flex",
			alignItems: "center",
			fontWeight: checked ? "bold" : "normal",
		}}
	>
		<input
			disabled={disabled}
			style={{ margin: 0, marginRight: 4 }}
			type="radio"
			checked={checked}
			onChange={() => setValue(value)}
		/>
		{`${value}`}
	</label>
);
